import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface MutationAction<T> {
  channel: string;
  send: () => Promise<T>;
  validate?: () => boolean;
  onStart?: () => void;
  onSuccess: (result: T, formCurrent: boolean) => void;
  onError: (error: unknown) => void;
}

/** Writes finish; only their originating account/tenant may apply UI effects. */
export function useScopedMutation(
  accountId: string | number | undefined,
  tenantId: string | number | undefined,
  form: object,
) {
  const scope = useMemo(
    () => ({ active: true, running: new Set<unknown>() }),
    [accountId, tenantId],
  );
  const latest = useRef({ scope, form });
  latest.current = { scope, form };
  const [busy, setBusy] = useState<{ scope: object; form: object }>();
  useEffect(() => {
    scope.active = true;
    return () => {
      scope.active = false;
    };
  }, [scope]);

  const runMutation = useCallback(
    async <T>(action: MutationAction<T>) => {
      const isCurrent = () => scope.active && latest.current.scope === scope;
      const originalForm = latest.current.form;
      const channel = action.channel === 'form' ? originalForm : action.channel;
      if (
        accountId == null ||
        tenantId == null ||
        !isCurrent() ||
        scope.running.has(channel)
      )
        return;
      if (action.validate && !action.validate()) return;
      scope.running.add(channel);
      if (action.channel === 'form') setBusy({ scope, form: originalForm });
      try {
        let result: T;
        try {
          action.onStart?.();
          result = await action.send();
        } catch (error) {
          if (isCurrent()) action.onError(error);
          else
            console.error(
              'Mutation failed after leaving its account/tenant context',
              error,
            );
          return;
        }

        if (isCurrent()) {
          try {
            action.onSuccess(result, latest.current.form === originalForm);
          } catch (error) {
            console.error(
              'Mutation succeeded, but its success handler failed',
              error,
            );
          }
        }
      } finally {
        scope.running.delete(channel);
        if (
          isCurrent() &&
          action.channel === 'form' &&
          latest.current.form === originalForm
        )
          setBusy(undefined);
      }
    },
    [scope, accountId, tenantId],
  );

  return {
    runMutation,
    formLoading: busy?.scope === scope && busy.form === form,
  };
}
